import type { CareUpdate } from "@/lib/api/care";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { REDACTED_LABEL } from "@/lib/redacted-display";
import {
  CareCard,
  Field,
  Pill,
  UserChip,
  formatCareDate,
} from "./care-primitives";

interface CareUpdateSectionProps {
  update: CareUpdate | null;
  onWrite: () => void;
}

export function CareUpdateSection({ update, onWrite }: CareUpdateSectionProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-h-9 items-center justify-between">
        <h2 className="font-heading text-base font-bold leading-6 tracking-[-0.4px] text-[#1f1e1d]">
          Care update
        </h2>
        <button
          type="button"
          onClick={onWrite}
          className="inline-flex min-h-9 items-center justify-center rounded-full border border-[rgba(31,30,29,0.16)] bg-white px-4 font-body text-[13px] font-semibold leading-4 text-[#1f1e1d]"
        >
          Write care update
        </button>
      </div>
      <CareCard className="pl-6 pr-4">
        {update ? (
          <CareUpdateBody update={update} />
        ) : (
          <p className="font-body text-base leading-6 text-[#58534b]">
            No care update on file yet.
          </p>
        )}
      </CareCard>
    </div>
  );
}

function CareUpdateBody({ update }: { update: CareUpdate }) {
  return (
    <>
      <CareUpdateFieldsView
        clinicalSummary={update.clinical_summary}
        diagnosticSupportStatement={update.diagnostic_support_statement}
        assessmentOfSymptoms={update.assessment_of_symptoms}
      />
      <div className="mt-4 flex items-center gap-2 border-t border-[#dfddd8] pt-3">
        {update.author ? <UserChip user={update.author} /> : null}
        <span className="font-mono text-xs font-medium leading-4 text-[#6e6b67]">
          {formatCareDate(update.submitted_at)}
        </span>
      </div>
    </>
  );
}

export function CareUpdateFieldsView({
  clinicalSummary,
  diagnosticSupportStatement,
  assessmentOfSymptoms,
  symptomsFirst = false,
}: {
  clinicalSummary: string | null;
  diagnosticSupportStatement: string | null;
  assessmentOfSymptoms: string | null;
  symptomsFirst?: boolean;
}) {
  const hidePhi = useDevOverridesStore((state) => state.hidePhiPii) === true;
  const summary = hidePhi && clinicalSummary ? REDACTED_LABEL : clinicalSummary;
  const statement =
    hidePhi && diagnosticSupportStatement
      ? REDACTED_LABEL
      : diagnosticSupportStatement;
  const symptoms = (
    <Field label="Assessment of symptoms">
      {assessmentOfSymptoms ? (
        <Pill tone={symptomsFirst ? "outline" : "ivory"} className="capitalize">
          {assessmentOfSymptoms}
        </Pill>
      ) : (
        "—"
      )}
    </Field>
  );
  return (
    <div className="flex flex-col gap-4">
      {symptomsFirst ? symptoms : null}
      <Field label="Clinical summary">{summary ?? "—"}</Field>
      <Field label="Diagnostic support statement">{statement ?? "—"}</Field>
      {symptomsFirst ? null : symptoms}
    </div>
  );
}
